import React, { useCallback, useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Card, Flex, Typography, Button, Form, Spin } from "antd"
import { WaiverStatusModal } from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/components/WaiverStatusModal"
import { useTranslation } from "react-i18next"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { useParentForm } from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/hooks/useParentForm"
import { WaiverOptions } from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/components/WaiverOptions"
import { WaiverDetails } from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/components/WaiverDetails"
import { ParentGuardianForm } from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/components/ParentGuardianForm"
import { MinorGuestForm } from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/components/MinorGuestForm"
import { SignDocumentModal } from "pagesV2/pro/Embedded/BookingConfirmation/modal/SignDocument"
import AddressIcon from "assets/icons/Address.svg"
import CalendarIcon from "assets/icons/calendar.svg"
import { postWaiverSubmission, checkSubmissionsAvailable } from "store/digital-waiver"
import { getBooking } from "store/bookings"
import { toaster } from "components/common/Toast"
import { routes } from "router"

import styles from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/index.module.css"

const { Title } = Typography

export default function TripBookingWaiverPage() {
  const { t } = useTranslation()
  const { uuid } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { loading: postLoading } = useSelector((store) => store?.digitalWaiver) || {}
  const bookingInfo = useSelector((store) => store.bookings.booking)
  const { id: tripBookingId, loading: bookingLoading } = bookingInfo
  const { remaining_adult_count, remaining_child_count, all_signed } = useSelector((store) => store.digitalWaiver.submissionsAvailable)

  // A booking can have multiple waivers attached, each flagged sign_by "primary" | "all".
  // The flow runs in two phases keyed off is_signed (waiver order is not relied upon):
  //   1. Primary phase — while any primary-only waiver is unsigned, the primary guest signs first.
  //      We skip the "who is participating" step and tag the submission user_type "primary".
  //   2. Guest phase — once the primary has signed (or there are no primary waivers), the "all"
  //      waivers are signed through the normal guest flow.
  const bookingWaivers = bookingInfo?.booking_waivers || []
  const primaryWaivers = bookingWaivers.filter((bookingWaiver) => bookingWaiver?.sign_by === "primary")
  const allWaivers = bookingWaivers.filter((bookingWaiver) => bookingWaiver?.sign_by === "all")
  const hasAllWaivers = allWaivers.length > 0
  const isPrimaryPhase = primaryWaivers.length > 0 && primaryWaivers.some((bookingWaiver) => !bookingWaiver?.is_signed)
  // Only show the "who is participating" step once we're past the primary phase and guests must sign.
  const showParticipating = !isPrimaryPhase && hasAllWaivers
  // A pure primary-only booking is spent once its waivers are signed — the link is no longer valid.
  const isPrimaryOnlyBooking = primaryWaivers.length > 0 && !hasAllWaivers
  const isPrimaryWaiverAlreadySigned = isPrimaryOnlyBooking && primaryWaivers.every((bookingWaiver) => bookingWaiver?.is_signed)
  // Every attached waiver satisfied → nothing left for anyone to sign, so show the already-signed
  // message instead of reopening the form (mirrors the shared-waiver "signed" state).
  const allBookingWaiversSigned = bookingWaivers.length > 0 && bookingWaivers.every((bookingWaiver) => bookingWaiver?.is_signed)

  // A primary-only waiver that the primary guest has already signed must never be presented to a
  // later signer (e.g. a guest signing the "all" waivers), so drop those documents client-side.
  const filterOutSignedPrimaryDocs = useCallback(
    (docs) => {
      const signedPrimaryWaiverIds = new Set(
        (bookingInfo?.booking_waivers || [])
          .filter((bookingWaiver) => bookingWaiver?.sign_by === "primary" && bookingWaiver?.is_signed)
          .map((bookingWaiver) => bookingWaiver?.booking_waiver_id)
      )
      return (docs || []).filter(
        (doc) => !(doc?.sign_by === "primary" && doc?.is_signed) && !signedPrimaryWaiverIds.has(doc?.booking_waiver_id)
      )
    },
    [bookingInfo]
  )

  const { selected, setSelected, parentData, setParentData, form } = useParentForm()
  const guardianForm = Form.useForm()[0]

  const [isFormValid, setIsFormValid] = useState(false)
  const [showAdultForm, setShowAdultForm] = useState(false)
  const [showGuardianForm, setShowGuardianForm] = useState(false)
  // During the primary phase we skip the "who is participating" step and show the adult form
  // straight away, so treat it as already revealed.
  const showAdultFormResolved = isPrimaryPhase || showAdultForm
  const [showSignDocumentModal, setShowSignDocumentModal] = useState(false)
  const [waiverSubmissionId, setWaiverSubmissionId] = useState(null)
  const [pendingDocuments, setPendingDocuments] = useState([])

  // Fetch trip booking to get numeric trip_booking_id
  useEffect(() => {
    dispatch(getBooking(uuid, true))
  }, [uuid]) // eslint-disable-line

  useEffect(() => {
    if (uuid) {
      dispatch(checkSubmissionsAvailable(uuid))
    }
  }, [uuid]) // eslint-disable-line

  useEffect(() => {
    if (isPrimaryPhase) {
      // During the primary phase the signer is always the adult primary guest
      setSelected("adult")
    } else if (remaining_child_count === 0) {
      setSelected("adult")
    } else if (remaining_adult_count === 0) {
      setSelected("guardian")
    }
  }, [remaining_adult_count, remaining_child_count, isPrimaryPhase]) // eslint-disable-line

  // Reset forms and validity when switching between adult and guardian
  useEffect(() => {
    if (selected === "adult" && form) {
      form.resetFields()
      setShowAdultForm(false)
    } else if (selected === "guardian" && guardianForm) {
      guardianForm.resetFields()
      setShowGuardianForm(false)
    }
    setIsFormValid(false)
  }, [selected]) // eslint-disable-line

  const checkAdultFormValidity = useCallback(() => {
    if (selected !== "adult" || !form) return false
    const values = form.getFieldsValue()
    const requiredFields = ["firstName", "lastName", "email", "dob", "phone"]
    return requiredFields.every((field) => {
      const value = values[field]
      if (field === "phone") return value?.fullNumber?.trim()
      return value?.toString().trim()
    })
  }, [selected, form])

  const checkGuardianFormValidity = useCallback(() => {
    if (selected !== "guardian" || !guardianForm) return false
    if (form) {
      const parentValues = form.getFieldsValue()
      const requiredFields = ["firstName", "lastName", "email", "dob", "phone", "relation"]
      const parentValid = requiredFields.every((field) => {
        const value = parentValues[field]
        if (field === "phone") return value?.fullNumber?.trim()
        return value?.toString().trim()
      })
      if (!parentValid) return false
    }
    const values = guardianForm.getFieldsValue()
    const minors = values.minors || []
    if (minors.length === 0) return false
    return minors.every((minor) => minor.fullName?.trim() && minor.dateOfBirth)
  }, [selected, guardianForm, form])

  useEffect(() => {
    let valid = false
    if (selected === "adult") valid = !showAdultFormResolved
    else if (selected === "guardian") valid = !showGuardianForm
    setIsFormValid(valid)
  }, [selected, showAdultFormResolved, showGuardianForm])

  useEffect(() => {
    if (selected === "adult" && showAdultFormResolved) setIsFormValid(checkAdultFormValidity())
  }, [selected, showAdultFormResolved, checkAdultFormValidity])

  useEffect(() => {
    if (selected === "guardian" && showGuardianForm) setIsFormValid(checkGuardianFormValidity())
  }, [selected, showGuardianForm, checkGuardianFormValidity])

  const handleNext = useCallback(async () => {
    if (selected === "adult" && !showAdultFormResolved) {
      setShowAdultForm(true)
      setIsFormValid(false)
    } else if (selected === "guardian" && !showGuardianForm) {
      setShowGuardianForm(true)
      setIsFormValid(false)
    } else if (selected === "adult" && showAdultFormResolved) {
      try {
        const values = await form.validateFields()
        const formData = new FormData()
        formData.append("waiver_submission[trip_booking_id]", tripBookingId)
        formData.append("waiver_submission[submission_for]", "adult")
        formData.append("waiver_submission[user_type]", isPrimaryPhase ? "primary" : "guest")
        formData.append("waiver_submission[waiver_participants_attributes][0][participant_type]", "adult")
        formData.append("waiver_submission[waiver_participants_attributes][0][first_name]", values.firstName || "")
        formData.append("waiver_submission[waiver_participants_attributes][0][last_name]", values.lastName || "")
        formData.append("waiver_submission[waiver_participants_attributes][0][email]", values.email || "")
        formData.append("waiver_submission[waiver_participants_attributes][0][phone_number]", values.phone?.fullNumber || "")
        const formattedDate = values.dob ? new Date(values.dob).toLocaleDateString("en-GB").replaceAll("/", "-") : ""
        formData.append("waiver_submission[waiver_participants_attributes][0][birthdate]", formattedDate)
        dispatch(postWaiverSubmission(formData)).then((res) => {
          toaster.success(res?.message)
          const docs = filterOutSignedPrimaryDocs(res?.data?.pending_waiver_submissions)
          setWaiverSubmissionId(res?.data?.id)
          setPendingDocuments(docs)
          setShowSignDocumentModal(true)
        })
      } catch {
        // Validation errors shown by AntD
      }
    } else if (selected === "guardian" && showGuardianForm) {
      try {
        const [guardianValues] = await Promise.all([form.validateFields(), guardianForm.validateFields()])
        const values = guardianForm.getFieldsValue()
        const minors = values.minors || []
        const formData = new FormData()
        formData.append("waiver_submission[trip_booking_id]", tripBookingId)
        formData.append("waiver_submission[submission_for]", "minor")
        formData.append("waiver_submission[guardian_first_name]", guardianValues.firstName || "")
        formData.append("waiver_submission[guardian_last_name]", guardianValues.lastName || "")
        formData.append("waiver_submission[guardian_email]", guardianValues.email || "")
        formData.append("waiver_submission[guardian_phone_number]", guardianValues.phone?.fullNumber || "")
        const formattedGuardianDate = guardianValues.dob ? new Date(guardianValues.dob).toLocaleDateString("en-GB").replaceAll("/", "-") : ""
        formData.append("waiver_submission[guardian_birthdate]", formattedGuardianDate)
        formData.append("waiver_submission[relation]", guardianValues.relation || "")
        minors.forEach((minor, index) => {
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][participant_type]`, "minor")
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][full_name]`, minor.fullName || "")
          const formattedDate = minor.dateOfBirth ? new Date(minor.dateOfBirth).toLocaleDateString("en-GB").replaceAll("/", "-") : ""
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][birthdate]`, formattedDate)
        })
        dispatch(postWaiverSubmission(formData)).then((res) => {
          toaster.success(res?.message)
          const docs = filterOutSignedPrimaryDocs(res?.data?.pending_waiver_submissions)
          setWaiverSubmissionId(res?.data?.id)
          setPendingDocuments(docs)
          setShowSignDocumentModal(true)
        })
      } catch {
        // Validation errors shown by AntD
      }
    }
  }, [selected, showAdultFormResolved, showGuardianForm, form, guardianForm, tripBookingId, dispatch, isPrimaryPhase, filterOutSignedPrimaryDocs])

  if (showSignDocumentModal) {
    return (
      <SignDocumentModal
        fullScreen
        showPageHeader
        open={true}
        uuid={uuid}
        waiverSubmissionId={waiverSubmissionId}
        pendingSubmission={pendingDocuments}
        bookingWaivers={pendingDocuments}
        onCancel={() => navigate(routes.clientBookingPath({ uuid }))}
        onNext={() => navigate(routes.clientWaiverThankYou())}
      />
    )
  }

  if (bookingLoading || !tripBookingId) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    )
  }

  if (isPrimaryWaiverAlreadySigned) return <WaiverStatusModal type="expired" />

  if (allBookingWaiversSigned || all_signed) return <WaiverStatusModal type="signed" />

  if (bookingInfo?.status === "canceled") return <WaiverStatusModal type="canceled" />

  return (
    <Flex gap={10} vertical className={`${styles.container} flex-1`} align="center" justify="center">
      <Flex className={styles.card} align="center" vertical>
        <Card>
          <Title level={3} className={styles.title}>
            {t("digital_waiver_and_contracts.waiver_form")}
          </Title>

          <WaiverDetails
            title={bookingInfo?.trip?.title || ""}
            date={bookingInfo?.booked_datetime || ""}
            location={bookingInfo?.trip?.address || ""}
            calendarIcon={CalendarIcon}
            addressIcon={AddressIcon}
          />

          {showParticipating && (
            <>
              <div className={`mt-4xl mb-s ${styles.subTitle}`}>{t("embedded.booking_confirmation.who_is_participation")}</div>

              <WaiverOptions selected={selected} onSelect={setSelected} className="mb-4xl" />
            </>
          )}

          {selected === "adult" && showAdultFormResolved && (
            <ParentGuardianForm
              form={form}
              parentData={parentData}
              setParentData={setParentData}
              t={t}
              validatePhoneNumber={validatePhoneNumber}
              setIsFormValid={setIsFormValid}
              title={isPrimaryPhase ? t("embedded.booking_confirmation.primary_guest") : undefined}
              dobDefaultPickerValue
            />
          )}
          {selected === "guardian" && showGuardianForm && (
            <>
              <ParentGuardianForm
                form={form}
                parentData={parentData}
                setParentData={setParentData}
                t={t}
                validatePhoneNumber={validatePhoneNumber}
                setIsFormValid={() => setIsFormValid(checkGuardianFormValidity())}
                showRelation={true}
                dobDefaultPickerValue
              />
              <MinorGuestForm t={t} form={guardianForm} setIsFormValid={() => setIsFormValid(checkGuardianFormValidity())} />
            </>
          )}

          <Flex justify="center" className={styles.buttonContainer}>
            <Button
              color="primary"
              variant="solid"
              size="large"
              className={styles.nextButton}
              onClick={handleNext}
              disabled={!isFormValid || postLoading}
              loading={postLoading}
            >
              {t("embedded.booking_confirmation.next")}
            </Button>
          </Flex>
        </Card>
      </Flex>
    </Flex>
  )
}
